import React, { useState } from 'react';

import { useLocation } from 'react-router-dom';

import { CardFantasyPrices, CardMedia, CardName, type TokenCardProps } from '@pkg/components';
import { useBuy } from '@pkg/hooks';
import { type WalletCollectiblesBalance } from '@pkg/types';

import { CollectionChainType } from '@app/client';
import { useNFTsPageStore } from '@app/store/zustand';

import { CardMarketplaceControls } from './CardMarketplaceControls';

import { CardInside, CardMediaHolder, PricesPadding, Wrapper } from './styles';

function capitalizeFirstLetter(value: string) {
    return value?.charAt(0).toUpperCase() + value?.slice(1);
}

export const MarketplaceCard = ({
    item,
    yatPageItem,
    groupItem,
    nftItem,
    cardSize,
    listPriceData,
    chain,
    hideCollectionData,
    isOwner,
    ...rest
}: TokenCardProps) => {
    const { image, contract, tokenId, rarityRank, media } = item || {};

    const { image_url, animation_url, name, hidden } = nftItem || {}; //item

    const [rawCollectionChain, collectionContract, token_id, collection_name] = nftItem?.id?.split('.') || [];
    const collectionChain = capitalizeFirstLetter(rawCollectionChain) as CollectionChainType;

    const [isHovering, setIsHovering] = useState(false);

    const { selectedItems } = useNFTsPageStore();
    const isSelected = selectedItems.includes(nftItem?.id || '');

    const { buyNow } = useBuy();

    const _chain = chain || collectionChain || CollectionChainType.Ethereum;
    const _image = image || image_url || animation_url;
    const _video = media || animation_url;
    const _contract = contract || collectionContract;
    const _tokenId = tokenId || token_id;
    const _name = name || item?.name || _tokenId;
    const _item_id = nftItem?.id;

    const marketplaceItem = {
        ...nftItem,
        id: _item_id,
        name: _name,
        image_url: _image,
        animation_url: _image,
        collection: _contract,
        token_id: _tokenId,
    } as WalletCollectiblesBalance;

    function handleBuyNow() {
        return buyNow({
            name: _name,
            collection: {
                chain: _chain,
                contractAddress: _contract,
                name: collection_name ?? '',
            },
            imageUrl: _image,
            price: {
                currency: 'eth',
                value: String(0), // TODO: Update price
            },
            tokenId: _tokenId,
        });
    }

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const isPreview = queryParams.has('preview');

    if ((!isOwner && hidden) || (isOwner && isPreview && hidden)) return null;

    return (
        <Wrapper onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)} $isHidden={hidden}>
            <CardInside $cardSize={cardSize} $height={rest.height} $isSelected={isSelected}>
                <CardMediaHolder>
                    <CardMedia
                        image_url={_image}
                        contract={_contract}
                        animation_url={_video}
                        token_id={_tokenId}
                        chain={_chain}
                        rarity_rank={rarityRank}
                        cardSize={cardSize}
                        isHovering={isHovering}
                    />

                    {marketplaceItem ? (
                        <CardMarketplaceControls
                            contractAddress={_contract}
                            tokenId={_tokenId}
                            nftItem={marketplaceItem}
                            isHovering={isHovering}
                            chain={_chain}
                            item_id={_item_id}
                            isOwner={isOwner && !isPreview}
                            onBuyNow={handleBuyNow}
                        />
                    ) : null}
                </CardMediaHolder>

                <CardName
                    name={_name}
                    contract={_contract}
                    token_id={_tokenId}
                    cardSize={cardSize}
                    hideCollectionData={hideCollectionData}
                    chain={_chain}
                />

                {(rest.list_price || rest.last_sell) && (
                    <PricesPadding>
                        <CardFantasyPrices
                            useTokenPricing
                            chain={_chain}
                            listPriceData={listPriceData}
                            floor_price_change={rest.floor_price_change}
                            listPriceEth={rest.list_price}
                            last_buy={rest.last_buy}
                            last_sell={rest.last_sell}
                        />
                    </PricesPadding>
                )}
            </CardInside>
        </Wrapper>
    );
};
